export const DocumentTypes = Object.freeze({
  TRADE_LICENSE: "TRADE_LICENSE",
  COMPANY_INCORPORATION: "COMPANY_INCORPORATION",
  PORT_OPERATOR_LICENSE: "PORT_OPERATOR_LICENSE",
  OTHER_DOCUMENT: "OTHER_DOCUMENT",
} as const);

export type DocumentType = (typeof DocumentTypes)[keyof typeof DocumentTypes];

export type VendorLicenseRequestDocument = {
  documentType: DocumentType;
  documentUrl: string;
  originalFileName?: string;
};

export type PortLicenseRequestDocument = {
  documentType: DocumentType;
  documentUrl: string;
  originalFileName?: string;
};

export type CompanyType = {
  companyTypeCode: string;
  companyTypeDesc: string;
};

export type PortOperatorLicenseRequestPayload = {
  documents: PortLicenseRequestDocument[];

  companyName: string;
  companyNameArabic: string;
  companyTypes: CompanyType[];
  companyAddress: string;
  telephoneNumber: string;
  mobileNumber: string;
  faxNumber: string;
  companyEmail: string;

  applicantEmail: string;
  applicantName: string;
  applicantPosition: string;

  natureOfBusinessInPof: string;
  numberOfEmployees: number;
  numberOfVehicles: number;

  tradingLicenseNumber: string | null;
  tradingLicenseValidityDate: string;
  tradeLicenseIssuingAuthority: string | null;

  purposeOfRequest: string;

  fujairahRegistered: boolean;
};

export type VendorLicenseRenewalRequestPayload = VendorLicenseRequestPayload & {
  id?: string;
};

export type PortOperatorLicenseRenewalRequestPayload = PortOperatorLicenseRequestPayload & {
  id?: string;
};

export type VendorLicenseRequestPayload = {
  documents: VendorLicenseRequestDocument[];
  companyName: string;
  companyNameArabic: string;
  companyTypes: CompanyType[];
  companyAddress: string;
  telephoneNumber: string;
  mobileNumber: string;
  faxNumber: string;
  companyEmail: string;
  applicantEmail: string;
  applicantName: string;
  applicantPosition: string;
  natureOfBusinessInPof: string;
  numberOfEmployees: number;
  numberOfVehicles: number;
  tradingLicenseNumber: string | null;
  tradingLicenseValidityDate: string;
  tradeLicenseIssuingAuthority: string | null;
  purposeOfRequest: string;
};

export type LicenseTaskStatus = "READY" | "CLAIMED" | "COMPLETED" | "CANCELLED" | "TERMINATED";

export type TaskDecisionStatus = "APPROVED" | "REJECTED" | "PENDING";

export type VendorLicenseTask = {
  taskId: string;
  workflowId: string;
  runId: string;
  role?: string | null;
  createdAt: string;
  status: LicenseTaskStatus;
  claimOwnerName: string | null;
};

export type VendorLicenseTaskDecision = {
  status: TaskDecisionStatus;
  startDate?: string;
  expiryDate?: string;
  numberOfAccountsAllowed?: number;
  companyId?: string;
  approvalNotes?: string;
  approvalAttachmentUrl?: string;
  rejectionNotes?: string;
  rejectionAttachmentUrl?: string;
};

export type LicenseDocumentResponse = {
  documentType: string;
  documentUrl: string;
  originalFileName?: string;
  [key: string]: unknown;
};

export type LicenseReceiptResponse = {
  receiptNumber: string;
  receiptDate: string;
  receiptAmount: number;
  receiptDocumentUrl: string;
  createdAt: string;
};

export interface LicenseResponse {
  refOrderNumber: number;
  createdAt: string;
  modifiedAt: string;
  modifiedBy: string;
  id: string;
  createdBy: string;
  companyId?: string | null;
  status: string;
  companyName: string;
  companyNameArabic: string;
  companyTypes: string | CompanyType[];
  companyAddress: string;
  telephoneNumber: string;
  mobileNumber: string;
  faxNumber: string;
  companyEmail: string;
  applicantEmail: string;
  applicantName: string;
  applicantPosition: string;
  natureOfBusinessInPof: string;
  numberOfEmployees: number;
  numberOfVehicles: number;
  tradingLicenseNumber: string | null;
  tradingLicenseValidityDate: string;
  vendorLicenseExpiryDate?: string;
  startDate?: string;
  expiryDate?: string;
  tradeLicenseIssuingAuthority: string | null;
  purposeOfRequest: string;
  documents?: LicenseDocumentResponse[];
  receipt?: LicenseReceiptResponse | null;
  fujairahRegistered?: boolean;

  task?: VendorLicenseTask | null;
  taskDecision?: VendorLicenseTaskDecision | null;
  [key: string]: unknown;
}

export interface VendorLicenseGridItem {
  id: string;
  refOrderNumber: number;
  companyName: string;
  companyTypes: string;
  natureOfBusinessInPof: string;
  tradingLicenseNumber: string;
  createdAt: string;
  status: string;
}

export interface VendorLicensesSearchResponse {
  items: VendorLicenseGridItem[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface VendorLicensesSearchRequest {
  companyName?: string[];
  companyTypes?: string[];
  natureOfBusinessInPof?: string[];
  tradingLicenseNumber?: string[];
  refOrderNumber?: number[];
  createdAt?: Array<{
    from?: string | null;
    to?: string | null;
  }>;
  status?: string[];
}

export type VendorLicenseCompletePayload = {
  status: TaskDecisionStatus;
  startDate?: string;
  expiryDate?: string;
  numberOfAccountsAllowed?: number;
  companyId?: string;
  approvalNotes?: string;
  approvalAttachmentUrl?: string;
  rejectionNotes?: string;
  rejectionAttachmentUrl?: string;
  submittedBy: string;
};

export interface CurrentLicenseGridItem {
  id: string;
  licenseType?: string;
  refOrderNumber?: number;
  companyTypes?: string;
  natureOfBusinessInPof?: string;
  tradingLicenseValidityDate?: string;
  vendorLicenseExpiryDate?: string;
  expiryDate?: string;
  tradingLicenseNumber?: string;
  createdAt?: string;
  status?: string;
}

export interface CurrentLicensesSearchResponse {
  items: CurrentLicenseGridItem[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface ServiceDeskLicenseGridItem {
  id: string;
  licenseType?: string;
  refOrderNumber?: number;
  companyName?: string;
  companyTypes?: string;
  natureOfBusinessInPof?: string;
  tradingLicenseNumber?: string;
  createdAt?: string;
  status?: string;
}

export interface ServiceDeskLicensesSearchResponse {
  items: ServiceDeskLicenseGridItem[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface ServiceDeskLicensesSearchRequest {
  companyName?: string[];
  companyTypes?: string[];
  natureOfBusinessInPof?: string[];
  tradingLicenseNumber?: string[];
  refOrderNumber?: number[];
  createdAt?: Array<{
    from?: string | null;
    to?: string | null;
  }>;
  status?: string[];
  licenseType?: string[];
}

export type PortOperatorLicenseReceiptPayload = {
  receiptNumber: string;
  receiptDate: string;
  receiptAmount: number;
  receiptDocumentUrl: string;
  createdAt: string;
};
